import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { RootStateAnswers } from "../../../questionnaire/store/answers-slice";
import { biometricInfoType } from "../../store/helper";
import CustomInputField from "../biometricTab/CustomInputField";
import CustomInputFieldDoubleNumeric from "../biometricTab/CustomInputFieldDoubleNumeric";
import { useProfileForm } from "./ProfileFormContext";

/**
 * Profile → Onboarding.
 *
 * Holds the biometric-questionnaire answers that used to be the Biometrics tab's
 * "My goals" and "Physical profile" columns, re-grouped into the design's
 * "My goals" and "Activity" sections and laid out as a three-column grid.
 *
 * The design's other two sections — Medical conditions and Behavior — are new
 * questions with no data behind them yet, so they are deliberately absent until
 * the biometric questionnaire is extended.
 *
 * Fields are the existing `CustomInputField`, which already owns the
 * category-driven labels and choices (from the stepper slice), the min/max
 * clamps, the Redux dirty tracking and the inline "Updated Info" hint. Save and
 * Cancel are in the ProfileScreen footer via ProfileFormContext.
 */

const OnboardingTab = () => {
  const theme: Theme = useTheme();
  const clientData = useSelector((state: RootStateAnswers) => state.answers);
  const { openPopperId, setOpenPopperId } = useProfileForm();

  const isMetric =
    clientData.biometricQuest.myAnswers.mass_measurement_unit === 1;

  const field = (category: string, type: "numeric" | "select" = "select") => (
    <CustomInputField
      key={category}
      type={type}
      category={category}
      openPopperId={openPopperId}
      setOpenPopperId={setOpenPopperId}
    />
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.section}>
        <Box sx={styles.sectionTitle(theme)}>My goals</Box>
        <Box sx={styles.grid}>
          {field(biometricInfoType.current_weight, "numeric")}
          {field(biometricInfoType.target_weight, "numeric")}
          {/* Imperial height is a ft+in pair, so it needs the double field. */}
          {isMetric ? (
            field(biometricInfoType.height, "numeric")
          ) : (
            <CustomInputFieldDoubleNumeric
              category={biometricInfoType.height}
              setOpenPopperId={setOpenPopperId}
            />
          )}
          {field(biometricInfoType.fat_loss_intensity)}
          {field(biometricInfoType.fat_mass_target)}
          {field(biometricInfoType.primary_fitness_goal)}
          {field(biometricInfoType.secondary_fitness_goal)}
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionTitle(theme)}>Activity</Box>
        <Box sx={styles.grid}>
          {field(biometricInfoType.physical_activity)}
          {field(biometricInfoType.occupation_type)}
          {field(biometricInfoType.exercise_frequency)}
          {field(biometricInfoType.resistance_training_level)}
          {field(biometricInfoType.cardio_training_level)}
          {field(biometricInfoType.interval_training_level)}
          {field(biometricInfoType.resistance_hypertrophy_focus_area)}
          {field(biometricInfoType.resistance_strength_focus_area)}
          {field(biometricInfoType.resistance_equipment)}
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingTab;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
  },
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  grid: {
    display: "grid",
    // minmax(0, …) so a long option label can't stretch its column — see the
    // note in nutritionTab/tabs/TileGrid.tsx.
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "start",
  },
};
